import momentTz from "moment-timezone";
import { findIana } from "windows-iana";

const UTC_OFFSET_LABEL = /\(UTC([+-]\d{2}:\d{2})\)/;

const parseOffsetFromLabel = (label) => {
  if (!label) return null;
  const match = UTC_OFFSET_LABEL.exec(label);
  if (!match?.[1]) return null;
  const offsetStr = match[1];
  const sign = offsetStr.startsWith("-") ? -1 : 1;
  const [hours, minutes] = offsetStr.replace(/[+-]/, "").split(":").map(Number);
  return sign * (hours * 60 + minutes);
};

const offsetAtIanaZone = (ianaZone, atDate) => {
  if (!atDate) {
    return momentTz.tz(ianaZone).utcOffset();
  }
  const source = momentTz.isMoment(atDate) ? atDate : momentTz(atDate);
  if (!source.isValid()) {
    return momentTz.tz(ianaZone).utcOffset();
  }
  return momentTz
    .tz(source.format("YYYY-MM-DD HH:mm:ss"), ianaZone)
    .utcOffset();
};

/**
 * UTC offset in minutes for a Windows timezone id, including DST.
 * Maps via windows-iana + moment-timezone (e.g. Pacific Standard Time →
 * America/Los_Angeles → -420 in PDT, -480 in PST). Falls back to the
 * "(UTC+/-HH:MM)" prefix on the option label when mapping fails.
 *
 * @param {string} timezoneValue - Windows timezone ID, e.g. "Pacific Standard Time"
 * @param {Array}  timezones     - Array of { value, label } timezone options
 * @param {string|Date|import("moment").Moment} [atDate] - instant to evaluate (booking date)
 * @returns {number} offset in minutes, or 0 if unresolved
 */
export const getOffsetMinutes = (timezoneValue, timezones, atDate) => {
  const tz = timezones?.find(
    (t) =>
      t.value === timezoneValue ||
      (t.value &&
        timezoneValue &&
        t.value.toLowerCase() === timezoneValue.toLowerCase())
  );
  const windowsId = tz?.value || timezoneValue;

  if (windowsId) {
    try {
      const ianaZones = findIana(windowsId);
      if (ianaZones?.length) {
        return offsetAtIanaZone(ianaZones[0], atDate);
      }
    } catch (_) {
      // fall through to IANA id / label parse
    }

    if (momentTz.tz.zone(windowsId)) {
      return offsetAtIanaZone(windowsId, atDate);
    }
  }

  const fromLabel = parseOffsetFromLabel(tz?.label) ?? parseOffsetFromLabel(timezoneValue);
  return fromLabel ?? 0;
};
